import { useCallback, useMemo } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  CreateTextNoteRequest,
  CreateTextNoteResponse,
  ProcessAudioRequest,
  ProcessAudioResponse,
  TranscribeAudioRequest,
  TranscribeAudioResponse,
} from 'core/api/client/NotesClient';
import { apiClient } from 'core/api/globals';
import { NotificationsService } from 'core/notifications';

import { AuthenticationScheme } from 'features/auth/constants';
import { useAssertFetchPermitted } from 'features/auth/hooks';

/**
 * Provides mutation functions for notes operations within a patient context.
 * @param patientId - The ID of the current patient (for query invalidation).
 */
export function useMutationNotes(patientId: number | null | undefined) {
  const queryClient = useQueryClient();
  const activeScheme = useAssertFetchPermitted([AuthenticationScheme.OIDC, AuthenticationScheme.SHARE]);

  const filesQueryKey = useMemo(() => (patientId == null || patientId === 0 ? null : ['files', 'by-patient-id', patientId]), [patientId]);

  const invalidateFilesQuery = useCallback(() => {
    if (filesQueryKey == null) return;
    queryClient.invalidateQueries({ queryKey: filesQueryKey });
  }, [queryClient, filesQueryKey]);

  const createTextNoteMutation = useMutation({
    mutationFn: async (request: CreateTextNoteRequest): Promise<CreateTextNoteResponse> => {
      const authMode = activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required';
      return await apiClient.notesClient.createTextNote(request, authMode);
    },
    onSuccess: (result: CreateTextNoteResponse) => {
      NotificationsService.displaySuccess(`Text note "${result.fileName}" created successfully`);

      // Add the new text note to the files cache if we have patient files
      if (filesQueryKey != null) {
        // Note: We would need to fetch the full file data to properly add it to cache
        // For now, just invalidate to trigger a refresh
        invalidateFilesQuery();
      }
    },
    onError: (error, request) => {
      NotificationsService.displayError('Failed to create text note');
      datadogLogs.logger.error(
        'Text note creation failed.',
        {
          patientId: request.patientId,
          contentLength: request.content?.length || 0,
          title: request.title,
        },
        error instanceof Error ? error : undefined,
      );
    },
  });

  const transcribeAudioMutation = useMutation({
    mutationFn: async ({ fileId, request }: { fileId: number; request?: TranscribeAudioRequest }): Promise<TranscribeAudioResponse> => {
      const authMode = activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required';
      return await apiClient.notesClient.transcribeAudio(fileId, request, authMode);
    },
    onSuccess: (result: TranscribeAudioResponse, { fileId }) => {
      NotificationsService.displaySuccess('Audio transcribed successfully');

      // If a text note was created from the transcription, invalidate files cache
      if (result.createdNoteFileId) {
        invalidateFilesQuery();
      }
    },
    onError: (error, { fileId, request }) => {
      NotificationsService.displayError('Failed to transcribe audio');
      datadogLogs.logger.error(
        'Audio transcription failed.',
        {
          fileId,
          createTextNote: request?.createTextNote || false,
          language: request?.language || 'default',
        },
        error instanceof Error ? error : undefined,
      );
    },
  });

  const processAudioWithAIMutation = useMutation({
    mutationFn: async ({ fileId, request }: { fileId: number; request: ProcessAudioRequest }): Promise<ProcessAudioResponse> => {
      const authMode = activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required';
      return await apiClient.notesClient.processAudioWithAI(fileId, request, authMode);
    },
    onSuccess: (result: ProcessAudioResponse) => {
      NotificationsService.displaySuccess('Audio processed with AI successfully');
    },
    onError: (error, { fileId, request }) => {
      NotificationsService.displayError('Failed to process audio with AI');
      datadogLogs.logger.error(
        'Audio AI processing failed.',
        {
          fileId,
          hasPromptTemplate: !!request.promptTemplate,
        },
        error instanceof Error ? error : undefined,
      );
    },
  });

  return {
    createTextNoteMutation,
    transcribeAudioMutation,
    processAudioWithAIMutation,
    invalidateFilesQuery,
  };
}
